import React, { FC, useState } from 'react';
import { useSelector } from "react-redux";
import { toast } from 'react-toastify';
import Commands from '../../../../constants/commands';
import { NETWORK_MODE } from '../../../../constants/network-constants';
import { HardwareSubCmds, MQTTHardwareSubCmds } from '../../../../constants/sub-commands';
import { useLocale } from '../../../../contexts/localeContext';
import { TableContextProvider } from '../../../../contexts/table/TableContextProvider';
import { TranslationKeys } from '../../../../contexts/TranslationKeys';
import columns from '../../../../models/ColumnDefinitions/DiscoveryColumns';
import { Controller } from '../../../../models/Controller';
import { ExecuteCommand } from '../../../../models/ExecuteCommand';
import { Filter } from '../../../../models/Filter';
import { ListData } from '../../../../models/ListData';
import { ListQuery } from '../../../../models/ListQuery';
import searchColumns from '../../../../models/search/DiscoverySearch';
import { useGlobalState } from '../../../../Providers';
import { getDiscoveries } from '../../../../services/discoveryService';
import { executeCommand } from '../../../../services/executeCommandService';
import { getErrorMessage, showError } from '../../../../services/utilities/ServiceError';
import { IAppState } from '../../../../store/types';
import { appendFilter } from '../../../../utilities/filters';
import ActivityFrame from '../../../activity-frame/activity-frame';
import { useDialog } from '../../../dialog/DialogContext';
import TableTab from '../../../tabs/table-tab';

interface SelectIPDeviceProps {
    onNext: (e) => void;
    onBack: (e) => void;
    setSelectedDevice: (selectDevice: Controller) => void;
    selectedDevice: Controller;
}

const SelectIPDevice: FC<SelectIPDeviceProps> = ({ onNext, onBack, setSelectedDevice, selectedDevice}) => {
    const { translate } = useLocale();
    const GlobalContext = useGlobalState();
    const isTlsEnabled = GlobalContext?.buildInfo.tlsMode === NETWORK_MODE.CLOUD || GlobalContext?.buildInfo.tlsMode === NETWORK_MODE.LAN;
    const user = useSelector((state: IAppState) => (state.userInfo));
    const [busy, setBusy] = useState(false);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const dialog = useDialog();
    const [selectedRows, setSelectedRows] = useState<Controller[]>([]);

    const scanNetwork = () => {
        const command: ExecuteCommand = {
            cmd: Commands.Discovery.label,
            cmdSubType: isTlsEnabled ? MQTTHardwareSubCmds.MQTT_Discovery : HardwareSubCmds.Discover_Controllers,
            userLoginId: user.id,
            fixedAddress: isTlsEnabled ? selectedDevice.fixedAddress : "",
            macAddress: isTlsEnabled ? selectedDevice.macAddress : "",
            jsonResponse: ""
        }

        setBusy(true);
        executeCommand(command)
            .then(() => {
                dataRefresh((listParameters) => getDiscoveries({ ...listParameters, filters: appendFilter(1, 'Device Type', 'deviceType', appendFilter(1, 'Discovery Group', 'discoveryGroup.id', [])) }));
            })
            .catch((error) => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) })
            })
            .finally(() => {
                setBusy(false);
            })
    }

    const handleBack = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
        if (onBack) {
            onBack(e);
        }
    };

    const handleNext = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>): Promise<void> => {
        try {
            let deviceFixedAddresses: string[] = selectedRows.map((controller: Controller) => {
                return controller.fixedAddress ?? "";
            }).filter(fixedAddress => fixedAddress !== "");

            setSelectedDevice({ ...selectedDevice, selectIPDevices: deviceFixedAddresses })
            onNext(e);
        } catch (error) {
            showError(error, toast.warn);
        }
    };

    const [dataRefresh, setDataRefresh] = useState(() => (dataMethod?: (query?: ListQuery) => Promise<ListData>) => { return });

    const manualDataRefresh = (refreshData: (dataMethod?: (query?: ListQuery) => Promise<ListData>) => void) => {
        setDataRefresh(() => refreshData);
    }

    const buildFilters = (filters: Filter[]) : Filter[] => {
        const deviceTypeFilter = appendFilter(1, 'Device Type', 'deviceType', filters);
        const discoveryGroupFilter = appendFilter(1, 'Discovery Group', 'discoveryGroup.id', deviceTypeFilter)
        const filterOutRS485 = appendFilter('RS485', 'Filter Out RS485', 'hostName', discoveryGroupFilter, '!=');
        return filterOutRS485;
    }

    return <div className="pt-3 pr-3 h-full relative">
        <ActivityFrame busy={busy} busyMessage={translate.byKey(TranslationKeys.discoveryMessage)} className={`flex ${busy ? 'h-full w-full justify-center' : ''} flex-wrap content-center`}>
            <div className='flex w-full' style={{ maxHeight: '410px' }}>
                <TableContextProvider
                    useFilter
                    usePagination
                    useTable
                    useSort
                    useSearch
                    dataMethod={(listParameters) => getDiscoveries({ ...listParameters, filters: buildFilters(listParameters?.filters ?? []) })}
                    tableDescriptor={columns}
                    searchDescriptor={searchColumns}
                    manualDataRefresh={manualDataRefresh}
                    canExportReport={false}>
                    <TableTab
                        title={translate.byKey(TranslationKeys.selectAnIpDeviceToInstall)}
                        selectedIds={selectedIds}
                        setSelectedIds={setSelectedIds}
                        setSelectedRows={setSelectedRows}
                        selectedRows={selectedRows}
                        cyName='selectDevice'/>
                </TableContextProvider>
            </div>
            <div className="flex justify-between w-full absolute bottom-0 pr-3 pb-3 ">
                <div>
                    <button
                        className={`py-1 px-4 mr-2 mt-3 rounded-full text-white bg-sidemenu-focus text-sm items-center`}
                        type="button"
                        onClick={scanNetwork}>{translate.byKey(TranslationKeys.scanSubnet)}</button>
                </div>
                <div>
                    <button
                        className={`secondary secondary-inverse mr-2 py-1 px-5`}
                        type="button"
                        onClick={handleBack}>{translate.byKey(TranslationKeys.back)}</button>
                    <button
                        className={`py-1 px-5 mt-3 secondary  text-white bg-portal-blue-primary-shade2`}
                        type="button"
                        onClick={handleNext}>{translate.byKey(TranslationKeys.next)}</button>
                </div>
            </div>
        </ActivityFrame>
    </div>
}

export default SelectIPDevice;
